import * as React from 'react';
import type { Placement } from 'react-bootstrap/esm/Overlay';

import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { CertificationStatus } from '@acceligentllc/shared/enums/certificationStatus';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import Tooltip from 'af-components/Tooltip';

import styles from './styles.module.scss';

interface WorkShiftType {
	accountId: number;
	certificationStatus: Nullable<string>;
	certificationReason: Nullable<string>;
	/** format: ISO_DATETIME */
	startTime: Nullable<string>;
	/** format: ISO_DATETIME */
	endTime: Nullable<string>;
	/** format: ISO_DATETIME */
	outdatedAt: Nullable<string>;
}

interface OwnProps {
	workShift: WorkShiftType;
	timeZoneOffset?: number;
	tooltipPlacement?: Placement;
}

const CertificationStatusDisplay: React.FC<OwnProps> = (props: OwnProps) => {
	const { workShift, timeZoneOffset, tooltipPlacement = 'right' } = props;

	const browserOffset = React.useMemo(() => (new Date()).getTimezoneOffset(), []);

	const {
		certificationStatus,
		certificationReason,
		startTime: shiftStartTime,
		endTime: shiftEndTime,
		outdatedAt,
	} = workShift;

	const [certColor, certStatus] = React.useMemo(() => {
		let _certColor = 'grey';
		let _certStatus = 'N/A';

		switch (certificationStatus) {
			case CertificationStatus.OPT_IN:
				_certStatus = 'Cert.Status: Yes';
				_certColor = 'green';
				break;
			case CertificationStatus.OPT_OUT:
				_certStatus = 'Cert.Status: No';
				_certColor = 'red';
				break;
			case CertificationStatus.OPT_OUT_WITH_REASON:
				_certStatus = 'Cert.Status: No with reason';
				_certColor = 'red';
				break;
			default:
				break;
		}

		if (outdatedAt) {
			_certColor = 'orange';
			_certStatus = 'Outdated';
		}

		return [_certColor, _certStatus];
	}, [certificationStatus, outdatedAt]);

	const shiftDuration = React.useMemo(() => {
		let shiftStartTimeTZ = shiftStartTime;
		let shiftEndTimeTZ = shiftEndTime;

		if (!shiftStartTime || !shiftEndTime) {
			return null;
		}

		if (timeZoneOffset) {
			shiftStartTimeTZ = shiftStartTimeTZ && TimeUtils.offsetTime(
				shiftStartTimeTZ,
				timeZoneOffset + (browserOffset ?? 0),
				'minutes',
				TimeFormat.ISO_DATETIME
			);
			shiftEndTimeTZ = shiftEndTimeTZ && TimeUtils.offsetTime(
				shiftEndTimeTZ,
				timeZoneOffset + (browserOffset ?? 0),
				'minutes',
				TimeFormat.ISO_DATETIME
			);
		}

		return `${TimeUtils.formatDate(shiftStartTimeTZ, TimeFormat.FULL_DATE, TimeFormat.ISO_DATETIME)} — 
			${TimeUtils.formatDate(shiftEndTimeTZ, TimeFormat.FULL_DATE, TimeFormat.ISO_DATETIME)}`;
	}, [browserOffset, shiftEndTime, shiftStartTime, timeZoneOffset]);

	const tooltipMessage = React.useMemo(() => {
		const shiftDrationTooltip = <span><b>Shift: </b>{shiftDuration}</span>;

		if (certificationReason && !outdatedAt) {
			return <div className={styles['cert-status-tooltip']}>
				<div>{shiftDrationTooltip}</div>
				<div>
					<b>Cert.Status: No with reason: </b>
					<span>{certificationReason}</span>
				</div>
			</div>;
		}

		return <div>{shiftDrationTooltip}</div>;

	}, [certificationReason, outdatedAt, shiftDuration]);

	if (shiftDuration) {
		return (
			<div className={styles['cert-status']}>
				<Tooltip className="tooltip" message={tooltipMessage} placement={tooltipPlacement}>
					<span className={`text-${certColor} ${styles['cert-status-element']}`}>
						{certStatus}
					</span>
				</Tooltip>
			</div>
		);
	}

	return (
		<div className={styles['cert-status']}>
			<div className={`text-${certColor} ${styles['cert-status-element']}`}>
				<span>{certStatus}</span>
			</div>
		</div>
	);
};

export default React.memo(CertificationStatusDisplay);
